// Runtime activity binding for the shared LIFF. The active event slug is
// decided at runtime (from the scanned store) and persisted in localStorage
// so a direct (no-scan) re-open lands on the last-scanned activity.
//
// A `?event=<slug>` deep link binds THIS TAB explicitly (sessionStorage),
// which wins over the shared localStorage value. localStorage is shared by
// every tab of the same origin, so without a per-tab override three tabs
// could not preview three different events at once. The same deep link is the
// production "event landing" entry — give each activity a share URL
// `liff.line.me/<liffId>?event=<slug>` for customers who arrive without
// scanning a store QR.
import api from "@/api/client";

const KEY = "activeEventSlug";        // shared, persistent (last-scanned store)
const TAB_KEY = "activeEventSlug.tab"; // per-tab override (sessionStorage)

let _slug: string | null = localStorage.getItem(KEY);
let _tabSlug: string | null = sessionStorage.getItem(TAB_KEY);

export function getActiveEventSlug(): string | null {
  // Per-tab deep-link binding wins over the shared last-scanned value.
  return _tabSlug || _slug;
}

export function setActiveEventSlug(slug: string | null): void {
  _slug = slug || null;
  if (_slug) localStorage.setItem(KEY, _slug);
  else localStorage.removeItem(KEY);
}

/** Bind THIS TAB to an explicit event slug (from a `?event=<slug>` deep link).
 *  Persisted in sessionStorage so the binding survives reload + SPA navigation
 *  within the tab but does NOT leak to other tabs (each can preview a
 *  different event). Also mirrored to the shared localStorage so a fresh
 *  re-open in a new webview with no sessionStorage still lands on the same
 *  event — matching last-scanned-store semantics for production share links. */
export function setTabEventSlug(slug: string): void {
  _tabSlug = slug;
  sessionStorage.setItem(TAB_KEY, slug);
  setActiveEventSlug(slug);
}

/** Boot-time resolver: bind this tab to an explicit `?event=<slug>` query if
 *  present. Synchronous — MUST run before loadAppConfig / any per-event fetch
 *  so the very first config call (and applyThemeColors) target the right
 *  event. An unknown slug is harmless: the backend falls back to featured. */
export function resolveDeepLinkEventOnBoot(): void {
  const slug = eventSlugFromLocation();
  if (slug) setTabEventSlug(slug);
}

/** Extract a numeric bar id from a /checkin/<barId> path. QR deep links are
 *  /checkin/123 or /checkin/123-2 (id-cups) — take the leading digits. */
export function barIdFromPath(pathname: string): number | null {
  const m = pathname.match(/\/checkin\/(\d+)/);
  return m ? parseInt(m[1], 10) : null;
}

/** Read the target event slug from the URL — first from `?event=<slug>`,
 *  else from a `?liff.state=<url-encoded path+query>` that LINE sometimes
 *  uses (the real `?event=` rides inside it). Returns null if neither has it.
 *  Pure: takes the raw search string so it is testable / SSR-safe. */
export function eventSlugFromLocation(search: string = location.search): string | null {
  const params = new URLSearchParams(search);
  const direct = params.get("event");
  if (direct) return direct;
  const liffState = params.get("liff.state");
  if (liffState) {
    // liff.state looks like "/?event=summer" or "?event=summer" (decoded).
    // 畸形的 liff.state(未編碼的 %)會讓 decodeURIComponent 拋 URIError —
    // 在開機路徑這會炸掉整個 app(白畫面),故 catch 後退回原字串。
    let decoded: string;
    try {
      decoded = decodeURIComponent(liffState);
    } catch {
      decoded = liffState;
    }
    const qIndex = decoded.indexOf("?");
    const inner = new URLSearchParams(qIndex >= 0 ? decoded.slice(qIndex) : decoded);
    const fromState = inner.get("event");
    if (fromState) return fromState;
  }
  return null;
}

/** Boot-time resolver: if entering via a store QR (/checkin/:barId) and the
 *  visitor is NOT yet bound to an activity, bind to that store's activity
 *  (first participation). If they are ALREADY bound to a DIFFERENT activity,
 *  do NOT rebind — leave the existing binding so Checkin.vue can hard-block the
 *  cross-event scan (集點只限目前活動). Must run BEFORE loadAppConfig / any
 *  per-event fetch so they send the right ?event. */
export async function resolveActiveEventOnBoot(): Promise<void> {
  const barId = barIdFromPath(location.pathname);
  if (barId == null) return;
  if (getActiveEventSlug()) return;  // 已綁活動 → 不被掃到的店改綁(跨活動由 Checkin 擋)
  try {
    const r = await api.get<{ event_id: number; event_slug: string }>(
      `/bars/${barId}/event`
    );
    if (r.data?.event_slug) setActiveEventSlug(r.data.event_slug);
  } catch {
    /* 404 / network → degrade to existing localStorage value */
  }
}

/** Boot-time cold-entry resolver: when STILL unbound after deep-link +
 *  store-QR resolution, fetch the active events. Exactly one active → bind it
 *  (skip the chooser). Zero or 2+ → stay unbound; the router guard then sends
 *  the visitor to /select-event. MUST run before loadAppConfig. */
export async function resolveColdEntryOnBoot(): Promise<void> {
  // Explicit `?event=` deep link this load is honoured as-is (already bound by
  // resolveDeepLinkEventOnBoot) — even a not-yet-active event, so operators can
  // preview/share a draft. Skip validation for it.
  if (eventSlugFromLocation()) return;
  try {
    const r = await api.get<Array<{ slug: string }>>("/events");
    const active = (r.data ?? []).map((e) => e.slug);
    const bound = getActiveEventSlug();
    if (bound && active.includes(bound)) return;   // remembered & still active → keep
    if (bound) {
      // Smart remember: the remembered event is no longer active (ended /
      // unpublished) → forget it (both layers) so we re-resolve below.
      _tabSlug = null;
      sessionStorage.removeItem(TAB_KEY);
      setActiveEventSlug(null);
    }
    if (active.length === 1) setActiveEventSlug(active[0]);
    // 0 or 2+ → leave unbound; the router guard routes to /select-event
  } catch {
    /* network → keep whatever binding exists; guard/home handle it */
  }
}
